"use client";

import * as React from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { createPortal } from "react-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAppSelector } from "@/store";
import { cn } from "@/src/lib/utils";
import { getCentrifuge } from "@/src/lib/centrifuge";
import type { UserEntity } from "@/types/canonical";
import { fetchRelayIceServers } from "./relayServers";
import { PeerConnectionSignaling } from "./PeerConnectionSignaling";
import { SignalChannel } from "./SignalChannel";
import type {
  MediaCallContextValue,
  MediaCallMessage,
  MediaCallState,
  MediaCallUser,
  MediaCallWindowState,
  MediaDeviceSelection,
} from "./types";

const MediaCallContext = React.createContext<MediaCallContextValue>({
  callUser: async () => undefined,
  isBusy: false,
  callState: "closed",
  activeCallUser: null,
});

let notificationAudio: HTMLAudioElement | null = null;

type MediaCallPosition = {
  x: number;
  y: number;
};

type MediaCallDragState = {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  originX: number;
  originY: number;
  width: number;
  height: number;
};

const MEDIA_CALL_WINDOW_MARGIN = 12;

export function useMediaCall(): MediaCallContextValue {
  return React.useContext(MediaCallContext);
}

export function MediaCallProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const t = useTranslations();
  const currentUser = useAppSelector((state) => state.auth.user);
  const [callState, setCallState] = React.useState<MediaCallState>("closed");
  const incomingExamCall = callState === "incoming" && pathname?.includes("/assignments/quiz/");
  const [windowState, setWindowState] = React.useState<MediaCallWindowState>("normalized");
  const [targetUser, setTargetUser] = React.useState<MediaCallUser | null>(null);
  const [incomingUser, setIncomingUser] = React.useState<MediaCallUser | null>(null);
  const [remoteStream, setRemoteStream] = React.useState<MediaStream | null>(null);
  const [localVideoStream, setLocalVideoStream] = React.useState<MediaStream | null>(null);
  const [isAudioSend, setIsAudioSend] = React.useState(true);
  const [isVideoSend, setIsVideoSend] = React.useState(false);
  const [isScreenSend, setIsScreenSend] = React.useState(false);
  const [isRemoteVideoExists, setIsRemoteVideoExists] = React.useState(false);
  const [deviceDialogOpen, setDeviceDialogOpen] = React.useState(false);
  const [devices, setDevices] = React.useState<MediaDeviceInfo[]>([]);
  const [deviceSelection, setDeviceSelection] = React.useState<MediaDeviceSelection>({});
  const [callError, setCallError] = React.useState<string | null>(null);
  const [windowPosition, setWindowPosition] = React.useState<MediaCallPosition | null>(null);

  const listenerChannelRef = React.useRef<SignalChannel | null>(null);
  const activeChannelRef = React.useRef<SignalChannel | null>(null);
  const targetChannelRef = React.useRef<SignalChannel | null>(null);
  const peerRef = React.useRef<PeerConnectionSignaling | null>(null);
  const mediaStreamRef = React.useRef<MediaStream | null>(null);
  const displayStreamRef = React.useRef<MediaStream | null>(null);
  const audioContextRef = React.useRef<AudioContext | null>(null);
  const listenTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const disconnectTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = React.useRef<MediaCallState>("closed");
  const sleepingRef = React.useRef(false);
  const deviceSelectionRef = React.useRef<MediaDeviceSelection>({});
  const callWindowRef = React.useRef<HTMLDivElement | null>(null);
  const dragStateRef = React.useRef<MediaCallDragState | null>(null);
  const clientIdRef = React.useRef<string>("");
  if (!clientIdRef.current) {
    clientIdRef.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  React.useEffect(() => {
    stateRef.current = callState;
  }, [callState]);

  React.useEffect(() => {
    deviceSelectionRef.current = deviceSelection;
  }, [deviceSelection]);

  // The call window renders into document.body, and its root is a <dialog>
  // opened with showModal() so it lives in the browser TOP LAYER. This is what
  // makes it render correctly while the proctoring SDK holds the exam in
  // fullscreen: a top-layer element paints ABOVE the fullscreen element and is
  // immune to the SDK's `:fullscreen` styles. A plain fixed element outside the
  // fullscreen subtree would be mis-composited (washed-out / offset).
  const [portalMounted, setPortalMounted] = React.useState(false);
  React.useEffect(() => setPortalMounted(true), []);

  const dialogRef = React.useRef<HTMLDialogElement | null>(null);

  React.useEffect(() => {
    return () => {
      stopNotification();
      closeAudioContext(audioContextRef);
    };
  }, []);

  const callerUser = incomingUser ?? targetUser;
  const isVisible = callState !== "closed";

  // Drive the <dialog> so it lives in the browser top layer while visible.
  React.useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (isVisible) {
      if (!dialog.open) {
        try {
          dialog.showModal();
        } catch {
          // showModal throws if already open or not connected — safe to ignore.
        }
      }
    } else if (dialog.open) {
      dialog.close();
    }
  }, [isVisible, portalMounted]);
  const isConnected = callState === "connected";
  const isCalling = callState === "calling" || callState === "incoming";

  React.useLayoutEffect(() => {
    if (!portalMounted || !isVisible || windowState !== "normalized") return;

    const element = callWindowRef.current;
    if (!element) return;

    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    setWindowPosition((currentPosition) => {
      const basePosition = currentPosition ?? getCenteredMediaCallPosition(rect.width, rect.height);
      const nextPosition = clampMediaCallPosition(basePosition, rect.width, rect.height);

      if (
        currentPosition &&
        currentPosition.x === nextPosition.x &&
        currentPosition.y === nextPosition.y
      ) {
        return currentPosition;
      }

      return nextPosition;
    });
  }, [isVisible, portalMounted, windowState]);

  React.useEffect(() => {
    if (!isVisible || windowState !== "normalized") return;

    const clampCurrentPosition = () => {
      const element = callWindowRef.current;
      if (!element) return;

      const rect = element.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      setWindowPosition((currentPosition) => {
        if (!currentPosition) return currentPosition;

        const nextPosition = clampMediaCallPosition(currentPosition, rect.width, rect.height);
        if (currentPosition.x === nextPosition.x && currentPosition.y === nextPosition.y) {
          return currentPosition;
        }

        return nextPosition;
      });
    };

    window.addEventListener("resize", clampCurrentPosition);
    window.visualViewport?.addEventListener("resize", clampCurrentPosition);

    return () => {
      window.removeEventListener("resize", clampCurrentPosition);
      window.visualViewport?.removeEventListener("resize", clampCurrentPosition);
    };
  }, [isVisible, windowState]);

  const handleCallWindowPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (windowState !== "normalized") return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (isMediaCallDragBlocked(event.target)) return;

      const element = callWindowRef.current;
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const originPosition = clampMediaCallPosition(
        windowPosition ?? { x: rect.left, y: rect.top },
        rect.width,
        rect.height,
      );

      dragStateRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        originX: originPosition.x,
        originY: originPosition.y,
        width: rect.width,
        height: rect.height,
      };

      setWindowPosition(originPosition);
      event.currentTarget.setPointerCapture(event.pointerId);
      event.preventDefault();
    },
    [windowPosition, windowState],
  );

  const handleCallWindowPointerMove = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    const nextPosition = clampMediaCallPosition(
      {
        x: dragState.originX + event.clientX - dragState.startClientX,
        y: dragState.originY + event.clientY - dragState.startClientY,
      },
      dragState.width,
      dragState.height,
    );

    setWindowPosition(nextPosition);
    event.preventDefault();
  }, []);

  const handleCallWindowPointerEnd = React.useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    dragStateRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const callWindowStyle = React.useMemo(() => {
    const style = { ["--media-caller-volume" as string]: 0 } as React.CSSProperties;

    if (windowState === "normalized" && windowPosition) {
      style.position = "fixed";
      style.left = `${windowPosition.x}px`;
      style.top = `${windowPosition.y}px`;
    }

    return style;
  }, [windowPosition, windowState]);

  const currentMediaUser = React.useMemo(
    () => (currentUser ? toMediaCallUser(currentUser) : null),
    [currentUser],
  );

  const resetCallState = React.useCallback((options?: { keepParticipants?: boolean; keepWindow?: boolean }) => {
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }

    peerRef.current?.destroy();
    peerRef.current = null;

    if (targetChannelRef.current) {
      targetChannelRef.current.destroy();
      targetChannelRef.current = null;
    }

    activeChannelRef.current = null;
    stopStream(mediaStreamRef.current);
    stopStream(displayStreamRef.current);
    mediaStreamRef.current = null;
    displayStreamRef.current = null;
    setRemoteStream(null);
    setLocalVideoStream(null);
    if (!options?.keepParticipants) {
      setIncomingUser(null);
      setTargetUser(null);
    }
    setIsAudioSend(true);
    setIsVideoSend(false);
    setIsScreenSend(false);
    setIsRemoteVideoExists(false);
    if (!options?.keepParticipants) setCallError(null);
    if (!options?.keepWindow) {
      setWindowState("normalized");
      setWindowPosition(null);
    }
    closeAudioContext(audioContextRef);
  }, []);

  const closeLater = React.useCallback(
    (nextState: MediaCallState, reason?: string) => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      stopNotification();
      resetCallState({ keepParticipants: true, keepWindow: true });
      setCallError(reason ?? null);
      setCallState(nextState);
      closeTimerRef.current = setTimeout(() => {
        resetCallState();
        setCallState("closed");
      }, 2000);
    },
    [resetCallState],
  );

  const handleConnectionState = React.useCallback(
    (state: RTCPeerConnectionState | RTCIceConnectionState) => {
      if (state === "checking" || state === "connecting") {
        setCallState("preparing");
        playNotification("connecting", true);
        return;
      }

      if (state === "connected" || state === "completed") {
        if (disconnectTimerRef.current) {
          clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = null;
        }
        setCallError(null);
        setCallState("connected");
        playNotification("connected");
        return;
      }

      if (state === "disconnected") {
        if (!disconnectTimerRef.current) {
          disconnectTimerRef.current = setTimeout(() => {
            disconnectTimerRef.current = null;
            if (!["closed", "declined", "hangup", "failed"].includes(stateRef.current)) {
              closeLater("failed", "WebRTC disconnected for too long");
            }
          }, 10000);
        }
        return;
      }

      if (
        ["failed", "closed"].includes(state) &&
        !["closed", "declined", "hangup", "failed"].includes(stateRef.current)
      ) {
        closeLater("failed", `WebRTC connection ${state}`);
      }
    },
    [closeLater],
  );

  const createPeer = React.useCallback(
    async (channel: SignalChannel, polite: boolean) => {
      const iceServers = await fetchRelayIceServers();
      const peer = new PeerConnectionSignaling({
        channel,
        iceServers,
        polite,
        onStateChange: handleConnectionState,
        onRemoteStream: (stream) => {
          setRemoteStream(stream);
          createAudioMeter(stream, audioContextRef);
        },
      });

      peer.initialize();
      peerRef.current = peer;
      return peer;
    },
    [handleConnectionState],
  );

  const publishControl = React.useCallback(async (message: MediaCallMessage) => {
    const channel = activeChannelRef.current;
    if (!channel) return;
    await channel.publish({ ...message, iam: clientIdRef.current });
  }, []);

  const addAudioSource = React.useCallback(async () => {
    const peer = peerRef.current;
    if (!peer) return;

    const audioStream = await requestUserMedia({
      audio: true,
      video: false,
      selection: deviceSelection,
    });
    const audioTrack = audioStream.getAudioTracks()[0] ?? null;
    const videoTracks = getLiveVideoTracks(mediaStreamRef.current);

    stopAudioTracks(mediaStreamRef.current);
    const stream = new MediaStream([...(audioTrack ? [audioTrack] : []), ...videoTracks]);
    mediaStreamRef.current = stream;
    setIsAudioSend(true);
    if (isVideoSend) setLocalVideoStream(stream);

    await peer.setTrack("audio", audioTrack);
    await publishControl({ event: "audio-added" });
  }, [deviceSelection, isVideoSend, publishControl]);

  const removeAudioSource = React.useCallback(async () => {
    const peer = peerRef.current;
    await peer?.disableTrack("audio");
    stopAudioTracks(mediaStreamRef.current);
    mediaStreamRef.current = new MediaStream(getLiveVideoTracks(mediaStreamRef.current));
    setIsAudioSend(false);
    await publishControl({ event: "audio-removed" });
  }, [publishControl]);

  const addVideoSource = React.useCallback(async () => {
    const peer = peerRef.current;
    if (!peer) return;

    if (isScreenSend) {
      stopStream(displayStreamRef.current);
      displayStreamRef.current = null;
      setIsScreenSend(false);
    }

    const existingAudioTracks = isAudioSend ? getLiveAudioTracks(mediaStreamRef.current) : [];
    const cameraStream = await requestUserMedia({
      audio: isAudioSend && existingAudioTracks.length === 0,
      video: true,
      selection: deviceSelection,
    });
    const newAudioTracks = cameraStream.getAudioTracks();
    const videoTrack = cameraStream.getVideoTracks()[0] ?? null;

    stopVideoTracks(mediaStreamRef.current);
    const stream = new MediaStream([
      ...existingAudioTracks,
      ...newAudioTracks,
      ...(videoTrack ? [videoTrack] : []),
    ]);
    mediaStreamRef.current = stream;
    setLocalVideoStream(stream);
    setIsVideoSend(true);

    if (newAudioTracks[0]) await peer.setTrack("audio", newAudioTracks[0]);
    await peer.setTrack("video", videoTrack);
    await publishControl({ event: "video-added" });
  }, [deviceSelection, isAudioSend, isScreenSend, publishControl]);

  const removeVideoSource = React.useCallback(async () => {
    const peer = peerRef.current;
    await peer?.disableTrack("video");
    stopVideoTracks(mediaStreamRef.current);
    mediaStreamRef.current = new MediaStream(getLiveAudioTracks(mediaStreamRef.current));
    setIsVideoSend(false);
    if (!isScreenSend) setLocalVideoStream(null);
    await publishControl({ event: "video-removed" });
  }, [isScreenSend, publishControl]);

  const addScreenSource = React.useCallback(async () => {
    const peer = peerRef.current;
    if (!peer) return;

    if (isVideoSend) {
      stopVideoTracks(mediaStreamRef.current);
      mediaStreamRef.current = new MediaStream(getLiveAudioTracks(mediaStreamRef.current));
      setIsVideoSend(false);
    }

    const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    const screenTrack = stream.getVideoTracks()[0] ?? null;
    displayStreamRef.current = stream;
    setLocalVideoStream(stream);
    setIsScreenSend(true);

    screenTrack?.addEventListener("ended", () => {
      void (async () => {
        const activePeer = peerRef.current;
        await activePeer?.disableTrack("video");
        stopStream(displayStreamRef.current);
        displayStreamRef.current = null;
        setIsScreenSend(false);
        setLocalVideoStream(null);
        await publishControl({ event: "video-removed" });
      })();
    });

    await peer.setTrack("video", screenTrack);
    await publishControl({ event: "video-added" });
  }, [isVideoSend, publishControl]);

  const removeScreenSource = React.useCallback(async () => {
    const peer = peerRef.current;
    await peer?.disableTrack("video");
    stopStream(displayStreamRef.current);
    displayStreamRef.current = null;
    setIsScreenSend(false);
    setLocalVideoStream(null);
    await publishControl({ event: "video-removed" });
  }, [publishControl]);

  const handleControlMessage = React.useCallback(
    (message: MediaCallMessage) => {
      if ("type" in message) return;
      // Centrifugo echoes publications back to the sender — ignore our own.
      if (message.iam && message.iam === clientIdRef.current) return;

      switch (message.event) {
        case "call": {
          // A call that ended is held in a closing state for 2s before reset.
          // Let a fresh incoming call interrupt that window instead of dropping it.
          const closing = ["declined", "hangup", "failed"].includes(stateRef.current);
          if (stateRef.current !== "closed" && !closing) return;
          if (closing) {
            if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
            resetCallState();
          }
          if (message.user) setIncomingUser(message.user);
          activeChannelRef.current = listenerChannelRef.current;
          setCallError(null);
          setCallState("incoming");
          setWindowState("normalized");
          break;
        }
        case "accept":
          if (stateRef.current !== "calling") return;
          void (async () => {
            setCallState("preparing");
            const peer = peerRef.current;
            if (!peer) return;
            const stream = await requestUserMedia({
              audio: true,
              video: false,
              selection: deviceSelectionRef.current,
            });
            stopStream(mediaStreamRef.current);
            mediaStreamRef.current = stream;
            setIsAudioSend(true);
            await peer.replaceStream(stream);
          })().catch(() => closeLater("failed"));
          break;
        case "decline":
          closeLater("declined");
          break;
        case "hangup":
          closeLater("hangup");
          break;
        case "audio-added":
          break;
        case "audio-removed":
          break;
        case "video-added":
          setIsRemoteVideoExists(true);
          break;
        case "video-removed":
          setIsRemoteVideoExists(false);
          break;
      }
    },
    [closeLater, resetCallState],
  );

  const callUser = React.useCallback(
    async (user: MediaCallUser) => {
      if (!currentMediaUser) throw new Error("Current user is not available");
      const targetUserId = Number(user.id);
      if (!Number.isFinite(targetUserId) || targetUserId <= 0) {
        throw new Error("Target user id is not available");
      }
      if (currentMediaUser.id === targetUserId) throw new Error("Cannot call yourself");
      if (stateRef.current !== "closed") throw new Error("Media call is already active");

      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);

      setTargetUser({ ...user, id: targetUserId });
      setIncomingUser(null);
      setCallError(null);
      setCallState("calling");
      setWindowState("normalized");
      playNotification("dial", true);

      const channel = new SignalChannel(`$private:mediacall.${targetUserId}`);
      targetChannelRef.current = channel;
      activeChannelRef.current = channel;
      channel.addHandler(handleControlMessage);

      try {
        await createPeer(channel, false);
        await waitForOnlinePeer(channel, targetUserId);
        await channel.publish({ event: "call", user: currentMediaUser, iam: clientIdRef.current });
      } catch (error) {
        const message = getErrorMessage(error);
        console.error("[MediaCall] call failed", {
          targetUserId,
          channel: channel.name,
          error,
        });
        playNotification("offline");
        closeLater("failed", message);
      }
    },
    [closeLater, createPeer, currentMediaUser, handleControlMessage],
  );

  const acceptCall = React.useCallback(async () => {
    const channel = listenerChannelRef.current;
    if (!channel) return;

    activeChannelRef.current = channel;
    setCallError(null);
    setCallState("preparing");

    try {
      const peer = await createPeer(channel, true);
      const stream = await requestUserMedia({
        audio: true,
        video: false,
        selection: deviceSelection,
      });
      stopStream(mediaStreamRef.current);
      mediaStreamRef.current = stream;
      setIsAudioSend(true);
      await peer.replaceStream(stream);
      await channel.publish({ event: "accept", iam: clientIdRef.current });
    } catch (error) {
      console.error("[MediaCall] accept failed", error);
      await publishControl({ event: "decline" });
      closeLater("failed", getErrorMessage(error));
    }
  }, [closeLater, createPeer, deviceSelection, publishControl]);

  const declineCall = React.useCallback(async () => {
    await publishControl({ event: "decline" });
    closeLater("declined");
  }, [closeLater, publishControl]);

  const hangupCall = React.useCallback(async () => {
    await publishControl({ event: "hangup" });
    closeLater("hangup");
  }, [closeLater, publishControl]);

  React.useEffect(() => {
    if (!currentMediaUser) return;

    const ensureListening = () => {
      if (listenTimerRef.current) clearTimeout(listenTimerRef.current);
      listenTimerRef.current = setTimeout(ensureListening, 10000);

      const centrifuge = getCentrifuge();
      if (!centrifuge?.isConnected()) return;

      const isVisiblePage = document.visibilityState === "visible";
      if (!isVisiblePage && sleepingRef.current) return;
      sleepingRef.current = false;

      if (!listenerChannelRef.current) {
        const channel = new SignalChannel(`$private:mediacall.${currentMediaUser.id}`);
        channel.addHandler(handleControlMessage);
        channel.connect();
        listenerChannelRef.current = channel;
      }

      void listenerChannelRef.current.presence().then((presence) => {
        const users = Object.values(presence).map((entry) => Number(entry.user));
        const parallels = users.filter((userId) => userId === currentMediaUser.id);

        if (parallels.length > 1 && !isVisiblePage && stateRef.current === "closed") {
          listenerChannelRef.current?.destroy();
          listenerChannelRef.current = null;
          sleepingRef.current = true;
        }
      });
    };

    ensureListening();
    document.addEventListener("visibilitychange", ensureListening);

    return () => {
      document.removeEventListener("visibilitychange", ensureListening);
      if (listenTimerRef.current) clearTimeout(listenTimerRef.current);
      listenerChannelRef.current?.destroy();
      listenerChannelRef.current = null;
      resetCallState();
    };
  }, [currentMediaUser, handleControlMessage, resetCallState]);

  React.useEffect(() => {
    if (!deviceDialogOpen) return;

    void navigator.mediaDevices.enumerateDevices().then(setDevices).catch(() => setDevices([]));
  }, [deviceDialogOpen]);

  React.useEffect(() => {
    if (!remoteStream) {
      setIsRemoteVideoExists(false);
      return;
    }

    const updateRemoteVideoState = () => setIsRemoteVideoExists(hasRenderableVideo(remoteStream));

    const videoTracks = remoteStream.getVideoTracks();
    updateRemoteVideoState();

    for (const track of videoTracks) {
      track.addEventListener("mute", updateRemoteVideoState);
      track.addEventListener("unmute", updateRemoteVideoState);
      track.addEventListener("ended", updateRemoteVideoState);
    }

    return () => {
      for (const track of videoTracks) {
        track.removeEventListener("mute", updateRemoteVideoState);
        track.removeEventListener("unmute", updateRemoteVideoState);
        track.removeEventListener("ended", updateRemoteVideoState);
      }
    };
  }, [remoteStream]);

  const hasRemoteVideo = Boolean(remoteStream && isRemoteVideoExists && hasRenderableVideo(remoteStream));
  const hasLocalVideo = Boolean(localVideoStream && hasLiveVideo(localVideoStream));

  const contextValue = React.useMemo(
    () => ({
      callUser,
      isBusy: callState !== "closed",
      callState,
      activeCallUser: callerUser,
    }),
    [callUser, callState, callerUser],
  );

  return (
    <MediaCallContext.Provider value={contextValue}>
      {children}
      {portalMounted && isVisible && createPortal(
        <>
          <MediaCallScopedStyles />
          <dialog
            ref={dialogRef}
            data-window-state={windowState}
            onCancel={(event) => event.preventDefault()}
            className={cn(
              "te-media-call-root fixed z-[9999]",
              windowState === "collapsed"
                ? "bottom-4 right-4 sm:bottom-6 sm:right-6"
                : "inset-0 flex h-[100dvh] w-screen items-center justify-center bg-black/30 sm:p-4",
            )}
            onClick={() => windowState !== "collapsed" && setWindowState("collapsed")}
          >
            <div
              ref={callWindowRef}
              className={cn(
                "relative overflow-hidden bg-[#172033] text-white shadow-2xl transition-[width,height,border-radius,box-shadow]",
                windowState === "collapsed"
                  ? "h-[104px] w-[168px] cursor-pointer rounded-lg"
                  : windowState === "maximized"
                    ? "h-[100dvh] w-screen rounded-none sm:h-[92dvh] sm:w-[92vw] sm:rounded-xl"
                    : "h-[100dvh] w-screen rounded-none sm:h-[min(560px,92dvh)] sm:w-[min(880px,92vw)] sm:rounded-xl",
                windowState === "normalized" && "cursor-grab touch-none active:cursor-grabbing",
              )}
              style={callWindowStyle}
              onPointerDown={handleCallWindowPointerDown}
              onPointerMove={handleCallWindowPointerMove}
              onPointerUp={handleCallWindowPointerEnd}
              onPointerCancel={handleCallWindowPointerEnd}
              onLostPointerCapture={handleCallWindowPointerEnd}
              onClick={(event) => {
                event.stopPropagation();
                if (windowState === "collapsed") setWindowState("normalized");
              }}
            >
              <div className="absolute inset-0 bg-[#101827]">
                {hasRemoteVideo ? (
                  <CallVideo
                    stream={remoteStream}
                    muted
                    className="absolute inset-0"
                  />
                ) : (
                  <ParticipantPlaceholder user={callerUser} compact={windowState === "collapsed"} />
                )}
              </div>
              <RemoteAudio stream={remoteStream} />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/45" />

              {windowState !== "collapsed" && hasLocalVideo && (
                <div
                  className={cn(
                    "absolute right-3 z-10 overflow-hidden rounded-2xl border border-white/20 bg-black shadow-2xl sm:right-5",
                    windowState === "maximized"
                      ? "bottom-24 h-28 w-40 sm:h-48 sm:w-72"
                      : "bottom-24 h-24 w-32 max-w-[38vw] sm:h-36 sm:w-52",
                  )}
                >
                  <CallVideo stream={localVideoStream} muted className="absolute inset-0" />
                  <div className="absolute bottom-2 left-2 rounded-full bg-black/55 px-2 py-1 text-[11px] font-medium text-white">
                    You
                  </div>
                </div>
              )}

              <div className="absolute left-4 top-4 z-10 flex items-center gap-3">
                <Avatar className="h-10 w-10 border border-white/20">
                  <AvatarImage src={callerUser?.photo ?? undefined} />
                  <AvatarFallback
                    className="text-sm font-semibold text-white"
                    style={{ backgroundColor: callerUser?.color ?? "#68748B" }}
                  >
                    {getInitials(callerUser)}
                  </AvatarFallback>
                </Avatar>
                {windowState !== "collapsed" && (
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">
                      {incomingExamCall ? t("label-trustexam-admin-calling") : getFullName(callerUser)}
                    </div>
                    {incomingExamCall && (
                      <div className="truncate text-xs text-white/80">{getFullName(callerUser)}</div>
                    )}
                    <div className="truncate text-xs text-white/70">
                      {getStatusLabel(callState, isVideoSend, isScreenSend, callError)}
                    </div>
                  </div>
                )}
              </div>

              {windowState !== "collapsed" && (
                <div className="absolute right-4 top-4 z-10 flex items-center gap-3">
                  <IconButton
                    icon="minimize"
                    tone="window"
                    label="Minimize"
                    onClick={() => setWindowState("collapsed")}
                  />
                  <IconButton
                    icon="maximize"
                    tone="window"
                    label={windowState === "maximized" ? "Normalize" : "Maximize"}
                    onClick={() => setWindowState(windowState === "maximized" ? "normalized" : "maximized")}
                  />
                  <IconButton
                    icon="close"
                    tone="window"
                    label="Close"
                    onClick={isConnected ? hangupCall : declineCall}
                  />
                </div>
              )}

              {windowState !== "collapsed" && (
                <div className="absolute bottom-0 left-0 right-0 z-10 flex flex-wrap items-center justify-center gap-4 bg-gradient-to-t from-black/70 to-transparent px-4 py-7">
                  {isCalling && callState === "incoming" && (
                    <IconButton icon="phone" label="Accept" accent="green" onClick={() => void acceptCall()} />
                  )}
                  {isCalling && (
                    <IconButton icon="phone-off" label="Decline" accent="red" onClick={() => void declineCall()} />
                  )}
                  {isConnected && (
                    <>
                      <IconButton icon="phone-off" label="Hang up" accent="red" onClick={() => void hangupCall()} />
                      <IconButton
                        icon={isAudioSend ? "mic" : "mic-off"}
                        label={isAudioSend ? "Mute microphone" : "Enable microphone"}
                        onClick={() => void (isAudioSend ? removeAudioSource() : addAudioSource())}
                      />
                      <IconButton
                        icon={isVideoSend ? "video" : "video-off"}
                        label={isVideoSend ? "Stop camera" : "Enable camera"}
                        onClick={() => void (isVideoSend ? removeVideoSource() : addVideoSource())}
                      />
                      <IconButton
                        icon={isScreenSend ? "monitor-x" : "monitor-up"}
                        label={isScreenSend ? "Stop screen share" : "Share screen"}
                        onClick={() => void (isScreenSend ? removeScreenSource() : addScreenSource())}
                      />
                      <IconButton icon="settings" label="Devices" onClick={() => setDeviceDialogOpen(true)} />
                    </>
                  )}
                </div>
              )}
            </div>
          </dialog>
        </>,
        document.body,
      )}

      <DeviceDialog
        open={deviceDialogOpen}
        onOpenChange={setDeviceDialogOpen}
        devices={devices}
        selection={deviceSelection}
        onSelectionChange={setDeviceSelection}
      />
    </MediaCallContext.Provider>
  );
}

type MediaCallIconName =
  | "minimize"
  | "maximize"
  | "close"
  | "phone"
  | "phone-off"
  | "mic"
  | "mic-off"
  | "video"
  | "video-off"
  | "monitor-up"
  | "monitor-x"
  | "settings";

function IconButton({
  icon,
  label,
  accent,
  tone = "call",
  onClick,
}: {
  icon: MediaCallIconName;
  label: string;
  accent?: "red" | "green";
  tone?: "call" | "window";
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      data-accent={accent}
      data-icon={icon}
      data-tone={tone}
      aria-label={label}
      title={label}
      className={cn(
        "te-media-call-button pointer-events-auto inline-grid cursor-pointer place-items-center rounded-full border-0 p-0 text-white shadow-none outline-none transition",
        "focus-visible:ring-2 focus-visible:ring-white/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#101827]",
        tone === "window" ? "h-12 w-12 sm:h-14 sm:w-14" : "h-14 w-14",
      )}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    />
  );
}

function MediaCallScopedStyles() {
  return (
    <style>{`
      .te-media-call-root .te-media-call-button {
        appearance: none !important;
        position: relative !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 56px !important;
        height: 56px !important;
        min-width: 56px !important;
        min-height: 56px !important;
        padding: 0 !important;
        border: 0 !important;
        border-radius: 9999px !important;
        background: rgba(255, 255, 255, 0.18) !important;
        color: #ffffff !important;
        line-height: 0 !important;
        text-align: center !important;
        box-shadow: none !important;
      }

      .te-media-call-root .te-media-call-button[data-tone="window"] {
        width: 48px !important;
        height: 48px !important;
        min-width: 48px !important;
        min-height: 48px !important;
      }

      @media (min-width: 640px) {
        .te-media-call-root .te-media-call-button[data-tone="window"] {
          width: 56px !important;
          height: 56px !important;
          min-width: 56px !important;
          min-height: 56px !important;
        }
      }

      .te-media-call-root .te-media-call-button[data-accent="red"] {
        background: #f01524 !important;
      }

      .te-media-call-root .te-media-call-button[data-accent="green"] {
        background: #15b85b !important;
      }

      .te-media-call-root .te-media-call-button:hover {
        background: rgba(255, 255, 255, 0.28) !important;
      }

      .te-media-call-root .te-media-call-button[data-accent="red"]:hover {
        background: #db1020 !important;
      }

      .te-media-call-root .te-media-call-button[data-accent="green"]:hover {
        background: #119e4d !important;
      }

      .te-media-call-root .te-media-call-button::before {
        content: "" !important;
        pointer-events: none !important;
        position: absolute !important;
        left: 50% !important;
        top: 50% !important;
        right: auto !important;
        bottom: auto !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 24px !important;
        height: 24px !important;
        min-width: 24px !important;
        min-height: 24px !important;
        max-width: 24px !important;
        max-height: 24px !important;
        margin: 0 !important;
        padding: 0 !important;
        background: currentColor !important;
        color: #ffffff !important;
        line-height: 0 !important;
        transform: translate(-50%, -50%) !important;
        -webkit-mask: var(--te-media-call-icon) center / contain no-repeat !important;
        mask: var(--te-media-call-icon) center / contain no-repeat !important;
      }

      .te-media-call-root .te-media-call-button[data-icon="minimize"] {
        --te-media-call-icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M5 12h14'/%3E%3C/svg%3E");
      }

      .te-media-call-root .te-media-call-button[data-icon="maximize"] {
        --te-media-call-icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M15 3h6v6'/%3E%3Cpath d='M9 21H3v-6'/%3E%3Cpath d='M21 3l-7 7'/%3E%3Cpath d='M3 21l7-7'/%3E%3C/svg%3E");
      }

      .te-media-call-root .te-media-call-button[data-icon="close"] {
        --te-media-call-icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M18 6 6 18'/%3E%3Cpath d='m6 6 12 12'/%3E%3C/svg%3E");
      }

      .te-media-call-root .te-media-call-button[data-icon="phone"] {
        --te-media-call-icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.19 18.85 19.5 19.5 0 0 1 5.15 12.81 19.79 19.79 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.91.33 1.8.62 2.65a2 2 0 0 1-.45 2.11L8.04 9.67a16 16 0 0 0 6.29 6.29l1.19-1.19a2 2 0 0 1 2.11-.45c.85.29 1.74.5 2.65.62A2 2 0 0 1 22 16.92z'/%3E%3C/svg%3E");
      }

      .te-media-call-root .te-media-call-button[data-icon="phone-off"] {
        --te-media-call-icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45c.86.29 1.75.5 2.67.62A2 2 0 0 1 22 16.92V20a2 2 0 0 1-2.18 2A19.86 19.86 0 0 1 11.19 18.93 19.5 19.5 0 0 1 5.07 12.81 19.86 19.86 0 0 1 2 4.18 2 2 0 0 1 4.06 2H7.2a2 2 0 0 1 2 1.72c.12.91.33 1.8.62 2.65a2 2 0 0 1-.45 2.11L8.1 9.67'/%3E%3Cpath d='M22 2 2 22'/%3E%3C/svg%3E");
      }

      .te-media-call-root .te-media-call-button[data-icon="mic"] {
        --te-media-call-icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z'/%3E%3Cpath d='M19 10v2a7 7 0 0 1-14 0v-2'/%3E%3Cpath d='M12 19v3'/%3E%3C/svg%3E");
      }

      .te-media-call-root .te-media-call-button[data-icon="mic-off"] {
        --te-media-call-icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12 2a3 3 0 0 0-3 3v7'/%3E%3Cpath d='M15 9.34V5a3 3 0 0 0-5.68-1.33'/%3E%3Cpath d='M17 16.95A7 7 0 0 1 5 12v-2'/%3E%3Cpath d='M19 10v2a7 7 0 0 1-.11 1.23'/%3E%3Cpath d='M12 19v3'/%3E%3Cpath d='M22 2 2 22'/%3E%3C/svg%3E");
      }

      .te-media-call-root .te-media-call-button[data-icon="video"] {
        --te-media-call-icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m16 13 5.22 3.48A.5.5 0 0 0 22 16.06V7.94a.5.5 0 0 0-.78-.42L16 11v2Z'/%3E%3Crect x='2' y='6' width='14' height='12' rx='2'/%3E%3C/svg%3E");
      }

      .te-media-call-root .te-media-call-button[data-icon="video-off"] {
        --te-media-call-icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M10.66 6H14a2 2 0 0 1 2 2v3l5.22-3.48A.5.5 0 0 1 22 7.94v8.12a.5.5 0 0 1-.78.42L16 13v.34'/%3E%3Cpath d='M14 18H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h.34'/%3E%3Cpath d='M22 2 2 22'/%3E%3C/svg%3E");
      }

      .te-media-call-root .te-media-call-button[data-icon="monitor-up"] {
        --te-media-call-icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.3' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='12' rx='2'/%3E%3Cpath d='M8 20h8'/%3E%3Cpath d='M12 16v4'/%3E%3Cpath d='M12 11V7'/%3E%3Cpath d='m9 10 3-3 3 3'/%3E%3C/svg%3E");
      }

      .te-media-call-root .te-media-call-button[data-icon="monitor-x"] {
        --te-media-call-icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.3' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='4' width='18' height='12' rx='2'/%3E%3Cpath d='M8 20h8'/%3E%3Cpath d='M12 16v4'/%3E%3Cpath d='M9 8l6 6'/%3E%3Cpath d='m15 8-6 6'/%3E%3C/svg%3E");
      }

      .te-media-call-root .te-media-call-button[data-icon="settings"] {
        --te-media-call-icon: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z'/%3E%3Ccircle cx='12' cy='12' r='3'/%3E%3C/svg%3E");
      }
    `}</style>
  );
}

function CallVideo({
  stream,
  muted,
  className,
}: {
  stream: MediaStream | null;
  muted: boolean;
  className?: string;
}) {
  const ref = React.useRef<HTMLVideoElement>(null);

  React.useEffect(() => {
    if (!ref.current) return;
    ref.current.srcObject = stream;
    void ref.current.play().catch(() => undefined);
  }, [stream]);

  if (!stream) return null;

  return (
    <video
      ref={ref}
      autoPlay
      playsInline
      muted={muted}
      className={cn("h-full w-full object-cover", className)}
    />
  );
}

function RemoteAudio({ stream }: { stream: MediaStream | null }) {
  const ref = React.useRef<HTMLAudioElement>(null);

  React.useEffect(() => {
    if (!ref.current) return;
    ref.current.srcObject = stream;
    void ref.current.play().catch(() => undefined);
  }, [stream]);

  return <audio ref={ref} autoPlay className="hidden" />;
}

function ParticipantPlaceholder({
  user,
  compact,
}: {
  user: MediaCallUser | null;
  compact: boolean;
}) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-[#101827]">
      <div className="flex flex-col items-center gap-4">
        <Avatar className={cn("border border-white/15 shadow-2xl", compact ? "h-16 w-16" : "h-28 w-28")}>
          <AvatarImage src={user?.photo ?? undefined} />
          <AvatarFallback
            className={cn("font-semibold text-white", compact ? "text-xl" : "text-4xl")}
            style={{ backgroundColor: user?.color ?? "#68748B" }}
          >
            {getInitials(user)}
          </AvatarFallback>
        </Avatar>
        {!compact && (
          <div className="max-w-[320px] truncate text-center text-lg font-semibold text-white">
            {getFullName(user)}
          </div>
        )}
      </div>
    </div>
  );
}

function DeviceDialog({
  open,
  onOpenChange,
  devices,
  selection,
  onSelectionChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  devices: MediaDeviceInfo[];
  selection: MediaDeviceSelection;
  onSelectionChange: (selection: MediaDeviceSelection) => void;
}) {
  const audioInputs = devices.filter((device) => device.kind === "audioinput");
  const videoInputs = devices.filter((device) => device.kind === "videoinput");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Call devices</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <label className="grid gap-2 text-sm">
            Microphone
            <select
              className="h-10 rounded-md border bg-background px-3 text-sm"
              value={selection.audioInputId ?? ""}
              onChange={(event) => {
                onSelectionChange({ ...selection, audioInputId: event.target.value || undefined });
              }}
            >
              <option value="">Default microphone</option>
              {audioInputs.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Microphone ${device.deviceId.slice(0, 6)}`}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-2 text-sm">
            Camera
            <select
              className="h-10 rounded-md border bg-background px-3 text-sm"
              value={selection.videoInputId ?? ""}
              onChange={(event) => {
                onSelectionChange({ ...selection, videoInputId: event.target.value || undefined });
              }}
            >
              <option value="">Default camera</option>
              {videoInputs.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Camera ${device.deviceId.slice(0, 6)}`}
                </option>
              ))}
            </select>
          </label>
        </div>
      </DialogContent>
    </Dialog>
  );
}

async function waitForOnlinePeer(
  channel: SignalChannel,
  targetUserId: number,
  attemptsLimit = 10,
): Promise<void> {
  await channel.waitUntilSubscribed();

  for (let attempt = 0; attempt <= attemptsLimit; attempt += 1) {
    const presence = await channel.presence();
    const users = Object.values(presence).map((entry) => Number(entry.user)).filter(Number.isFinite);

    if (users.includes(targetUserId)) {
      return;
    }


    await new Promise((resolve) => setTimeout(resolve, 1000));
  }

  throw new Error(`Target user ${targetUserId} is not subscribed to ${channel.name}`);
}

async function requestUserMedia({
  audio,
  video,
  selection,
}: {
  audio: boolean;
  video: boolean;
  selection: MediaDeviceSelection;
}): Promise<MediaStream> {
  const constraints: MediaStreamConstraints = {
    audio: audio
      ? selection.audioInputId
        ? { deviceId: { exact: selection.audioInputId } }
        : true
      : false,
    video: video
      ? {
          width: { ideal: 640 },
          height: { ideal: 480 },
          ...(selection.videoInputId ? { deviceId: { exact: selection.videoInputId } } : {}),
        }
      : false,
  };

  return navigator.mediaDevices.getUserMedia(constraints);
}

function stopStream(stream: MediaStream | null): void {
  stream?.getTracks().forEach((track) => track.stop());
}

function stopAudioTracks(stream: MediaStream | null): void {
  stream?.getAudioTracks().forEach((track) => track.stop());
}

function stopVideoTracks(stream: MediaStream | null): void {
  stream?.getVideoTracks().forEach((track) => track.stop());
}

function getLiveAudioTracks(stream: MediaStream | null): MediaStreamTrack[] {
  return stream?.getAudioTracks().filter((track) => track.readyState === "live") ?? [];
}

function getLiveVideoTracks(stream: MediaStream | null): MediaStreamTrack[] {
  return stream?.getVideoTracks().filter((track) => track.readyState === "live") ?? [];
}

function hasLiveVideo(stream: MediaStream | null): boolean {
  return getLiveVideoTracks(stream).length > 0;
}

function hasRenderableVideo(stream: MediaStream | null): boolean {
  return stream?.getVideoTracks().some((track) => track.readyState === "live" && !track.muted) ?? false;
}

function getCenteredMediaCallPosition(width: number, height: number): MediaCallPosition {
  const viewport = getMediaCallViewport();
  return {
    x: (viewport.width - width) / 2,
    y: (viewport.height - height) / 2,
  };
}

function clampMediaCallPosition(
  position: MediaCallPosition,
  width: number,
  height: number,
): MediaCallPosition {
  const viewport = getMediaCallViewport();
  const maxX = Math.max(MEDIA_CALL_WINDOW_MARGIN, viewport.width - width - MEDIA_CALL_WINDOW_MARGIN);
  const maxY = Math.max(MEDIA_CALL_WINDOW_MARGIN, viewport.height - height - MEDIA_CALL_WINDOW_MARGIN);

  return {
    x: width >= viewport.width
      ? 0
      : Math.min(Math.max(position.x, MEDIA_CALL_WINDOW_MARGIN), maxX),
    y: height >= viewport.height
      ? 0
      : Math.min(Math.max(position.y, MEDIA_CALL_WINDOW_MARGIN), maxY),
  };
}

function getMediaCallViewport(): { width: number; height: number } {
  return {
    width: window.visualViewport?.width ?? window.innerWidth,
    height: window.visualViewport?.height ?? window.innerHeight,
  };
}

function isMediaCallDragBlocked(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(
    target.closest(
      [
        "button",
        "a",
        "input",
        "select",
        "textarea",
        "[role='button']",
        "[data-media-call-no-drag='true']",
      ].join(","),
    ),
  );
}

function playNotification(type: "dial" | "busy" | "connected" | "connecting" | "offline", loop = false): void {
  stopNotification();

  notificationAudio = new Audio(`/audio/media-caller/${type}.ogg`);
  notificationAudio.loop = loop;
  void notificationAudio.play().catch(() => undefined);
}

function stopNotification(): void {
  if (!notificationAudio) return;
  notificationAudio.pause();
  notificationAudio.currentTime = 0;
  notificationAudio = null;
}

function closeAudioContext(audioContextRef: React.MutableRefObject<AudioContext | null>): void {
  if (audioContextRef.current && audioContextRef.current.state !== "closed") {
    void audioContextRef.current.close();
  }
  audioContextRef.current = null;
}

function createAudioMeter(
  stream: MediaStream,
  audioContextRef: React.MutableRefObject<AudioContext | null>,
): void {
  try {
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      void audioContextRef.current.close();
    }

    const audioContext = new AudioContext();
    audioContextRef.current = audioContext;

    const source = audioContext.createMediaStreamSource(stream);
    const gainNode = audioContext.createGain();
    const processor = audioContext.createScriptProcessor(2048, 1, 1);

    source.connect(processor);
    source.connect(gainNode);
    gainNode.gain.value = 0;
    gainNode.connect(audioContext.destination);
    processor.connect(audioContext.destination);

    processor.onaudioprocess = (event) => {
      const inputData = event.inputBuffer.getChannelData(0);
      let total = 0;

      for (let index = 0; index < inputData.length; index += 1) {
        total += Math.abs(inputData[index]);
      }

      document.documentElement.style.setProperty(
        "--media-caller-volume",
        String(Math.sqrt(total / inputData.length) * 100),
      );
    };
  } catch {
    // Audio meter is cosmetic. Calls should continue without it.
  }
}

function toMediaCallUser(user: UserEntity): MediaCallUser {
  return {
    id: user.id,
    firstname: user.firstname,
    lastname: user.lastname,
    patronymic: user.patronymic,
    photo: user.photo_thumb?.medium ?? user.photo,
    color: user.color,
    initials: getInitials(user),
  };
}

function getFullName(user: MediaCallUser | null): string {
  if (!user) return "Unknown user";
  const name = [user.firstname, user.lastname].filter(Boolean).join(" ").trim();
  return name || "Unknown user";
}

function getInitials(user: MediaCallUser | UserEntity | null): string {
  const explicit = "initials" in (user ?? {}) ? (user as MediaCallUser | null)?.initials : null;
  if (explicit) return explicit;

  const first = user?.firstname?.[0] ?? "";
  const last = user?.lastname?.[0] ?? "";
  return `${first}${last}`.toUpperCase() || "??";
}

function getStatusLabel(
  state: MediaCallState,
  video: boolean,
  screen: boolean,
  error: string | null,
): string {
  if (state === "failed" && error) return error;
  if (screen) return "Screen share enabled";
  if (video) return "Camera enabled";
  if (state === "calling") return "Calling";
  if (state === "incoming") return "Incoming call";
  if (state === "preparing") return "Establishing connection";
  if (state === "declined") return "Call declined";
  if (state === "hangup") return "Call ended";
  if (state === "failed") return "Connection failed";
  return "Connected";
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (typeof error === "string" && error.trim()) return error;
  return "Connection failed";
}
